import { AttachmentType, DbotEventDispatcher } from "@/dbot/common/commonDomain";
import {
  FileUploadConfig,
  isFileUploadValidationError,
  toFileWithId,
  toFormData,
  UploadedAttachment,
  validateFiles,
} from "@/fileUpload/fileUploadDomain";
import { AxiosInstance } from "axios";
import { action } from "mobx";
import { logDebug } from "@/utils/log";
import { showAlert, showError, showGeneralError } from "@/events/eventsDomain";
import { createFileTypeValidationErrorMessage } from "@/fileUpload/fileUploadErrorMessages";
import { UpdateProjectResponse } from "@/dbot/project/projectDomain";
import { isExtensionImage, isExtensionOffice, isExtensionPdf } from "@/utils/files.ts";
import { ConversionModel } from "@/fileUpload/ConversionModel.ts";
import { isOk } from "@/common/common.ts";

export const uploadCustomDocumentBlob = ({
  projectId,
  documentId,
  orgNo,
  files,
  fileUploadConfig,
  dispatch,
  http,
  onSuccess,
  onError,
  onConversionStart,
  onProgress,
}: {
  projectId: string;
  documentId: string;
  orgNo?: string;
  files: File[];
  fileUploadConfig: FileUploadConfig;
  dispatch: DbotEventDispatcher;
  http: AxiosInstance;
  onSuccess: (res: UpdateProjectResponse) => void;
  onError: () => void;
  onConversionStart?: (model: ConversionModel) => void;
  onProgress?: (progress: number) => void;
}) => {
  if (!files.length) {
    onError();
    return;
  }
  if (files.length > 1) {
    dispatch([showError({ key: "dbot.custom-document.blob.too-many-files-error" })]);
    onError();
    return;
  }
  validateFiles(files, fileUploadConfig)
    .then((validatedFiles) => {
      const officeFiles = validatedFiles.filter(
        (f) => isExtensionOffice(f.name) || isExtensionImage(f.name),
      );
      const pdfFiles = validatedFiles.filter((f) => isExtensionPdf(f.name));

      officeFiles.forEach((file) =>
        uploadOfficeAsDocumentBlob({
          projectId,
          documentId,
          orgNo,
          file,
          dispatch,
          http,
          onSuccess,
          onError,
          onConversionStart,
          onProgress,
        }),
      );

      if (pdfFiles.length > 0 && orgNo) {
        const formData = toFormData(pdfFiles);
        http
          .post<UpdateProjectResponse>(
            `/project/${projectId}/document/${documentId}/upload-custom-document?orgNo=${orgNo}`,
            formData,
            { onUploadProgress: (e) => onProgress?.(e.progress ?? 0) },
          )
          .then(action((r) => onSuccess(r.data)))
          .catch(() => {
            dispatch([showGeneralError()]);
            onError();
          });
      }
    })
    .catch((err) => {
      if (isFileUploadValidationError(err)) {
        logDebug(err, "Invalid attachments", { documentId, projectId });
        dispatch([showError(createFileTypeValidationErrorMessage(err))]);
      } else {
        dispatch([showGeneralError()]);
      }
      onError();
    });
};

export const deleteDocumentBlob = ({
  projectId,
  documentId,
  orgNo,
  http,
  onSuccess,
  onError,
}: {
  projectId: string;
  documentId: string;
  orgNo?: string;
  http: AxiosInstance;
  onSuccess: (res: UpdateProjectResponse) => void;
  onError?: () => void;
}) => {
  http
    .delete<UpdateProjectResponse>(
      `/project/${projectId}/document/${documentId}?orgNo=${orgNo}`,
      {},
    )
    .then((r) => onSuccess(r.data))
    .catch(() => onError?.());
};

const uploadOfficeAsDocumentBlob = ({
  projectId,
  documentId,
  orgNo,
  file,
  dispatch,
  http,
  onSuccess,
  onError,
  onConversionStart,
  onProgress,
}: {
  projectId: string;
  documentId: string;
  orgNo?: string;
  file: File;
  dispatch: DbotEventDispatcher;
  http: AxiosInstance;
  onSuccess: (res: UpdateProjectResponse) => void;
  onError: () => void;
  onConversionStart?: (model: ConversionModel) => void;
  onProgress?: (progress: number) => void;
}) => {
  const { id } = toFileWithId(file);
  const formData = new FormData();
  formData.append("files", file);
  formData.append("ids", id);
  formData.set("reference", documentId);
  formData.set("attachmentType", AttachmentType.UPLOADED_FILE);
  formData.set("overrideExistingAttachmentType", "false");
  http
    .post<UploadedAttachment[]>(`/project/${projectId}/attachment`, formData, {
      onUploadProgress: (e) => onProgress?.(e.progress ?? 0),
    })
    .then((res) => {
      const attachmentId = res.data[0]?.id ?? id;
      const model = new ConversionModel(
        http,
        { type: "attachmentId", id: attachmentId },
        file.name,
        dispatch,
      );

      const onConversionSuccess = () => {
        if (!isOk(model.keepDiscardResult)) {
          onError();
          return;
        }
        const { data: pdfBlob, filename } = model.keepDiscardResult.data;
        const pdfFile = new File([pdfBlob], filename, { type: "application/pdf" });
        const pdfFormData = toFormData([pdfFile]);
        http
          .post<UpdateProjectResponse>(
            `/project/${projectId}/document/${documentId}/upload-custom-document?orgNo=${orgNo}`,
            pdfFormData,
          )
          .then(action((r) => onSuccess(r.data)))
          .catch(() => {
            dispatch([showGeneralError()]);
            onError();
          });
      };

      const onConversionFailure = () => {
        dispatch([
          showAlert(
            { key: "file-preview.convert-to-pdf.error", params: { filename: file.name } },
            "info",
          ),
        ]);
        http.delete(`/project/${projectId}/attachment/${attachmentId}`).catch(() => {});
        onError();
      };

      onConversionStart?.(model);
      model.onConvert(true, onConversionSuccess, onConversionFailure);
    })
    .catch(() => {
      dispatch([showGeneralError()]);
      onError();
    });
};
